// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useMemo } from 'react';
import "./SessionAnalytics.scss";
import { useTranslation } from "react-i18next";
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function SessionAnalytics({ logs, t, companies, getCompanyDisplayName }) {
  const { t: translate } = useTranslation();
  const tFunction = t || translate;

  // Process logs to extract session data
  const processedData = useMemo(() => {
    if (!logs || logs.length === 0) {
      return {
        totalSessions: 0,
        averageSessionDuration: 0,
        averageEventsPerSession: 0,
        sessionDurations: [],
        sessionsByHour: Array(24).fill(0),
        sessionsEventsCount: []
      };
    }

    // Group logs by user and sort by timestamp
    const userLogs = {};
    logs.forEach(log => {
      if (!userLogs[log.user]) {
        userLogs[log.user] = [];
      }
      userLogs[log.user].push(log);
    });

    // Sort each user's logs by timestamp
    Object.keys(userLogs).forEach(user => {
      userLogs[user].sort((a, b) => a.timestamp.toDate() - b.timestamp.toDate());
    });

    // Session timeout - 30 minutes of inactivity
    const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
    
    // Identify sessions
    const sessions = [];
    let sessionDurations = [];
    let sessionsEventsCount = [];
    const sessionsByHour = Array(24).fill(0);

    Object.keys(userLogs).forEach(user => {
      const userEvents = userLogs[user];
      
      // Skip if user has no events
      if (userEvents.length === 0) return;
      
      let currentSession = {
        user,
        events: [userEvents[0]],
        startTime: userEvents[0].timestamp.toDate(),
        endTime: userEvents[0].timestamp.toDate()
      };
      
      for (let i = 1; i < userEvents.length; i++) {
        const currentEvent = userEvents[i];
        const currentTime = currentEvent.timestamp.toDate();
        const lastEventTime = currentSession.endTime;
        const timeDiff = currentTime.getTime() - lastEventTime.getTime();
        
        if (timeDiff > SESSION_TIMEOUT) {
          // End current session and start a new one
          sessions.push(currentSession);
          
          // Calculate session duration in minutes
          const duration = (currentSession.endTime.getTime() - currentSession.startTime.getTime()) / 1000 / 60;
          sessionDurations.push(duration);
          sessionsEventsCount.push(currentSession.events.length);
          
          // Record session start hour
          const hour = currentSession.startTime.getHours();
          sessionsByHour[hour]++;
          
          // Start new session
          currentSession = {
            user,
            events: [currentEvent],
            startTime: currentTime,
            endTime: currentTime
          };
        } else {
          // Continue current session
          currentSession.events.push(currentEvent);
          currentSession.endTime = currentTime;
        }
      }
      
      // Don't forget the last session
      sessions.push(currentSession);
      
      // Calculate session duration in minutes for the last session
      const duration = (currentSession.endTime.getTime() - currentSession.startTime.getTime()) / 1000 / 60;
      sessionDurations.push(duration);
      sessionsEventsCount.push(currentSession.events.length);
      
      // Record session start hour
      const hour = currentSession.startTime.getHours();
      sessionsByHour[hour]++;
    });

    // Calculate average session duration in minutes
    const totalDuration = sessionDurations.reduce((acc, duration) => acc + duration, 0);
    const averageSessionDuration = sessions.length > 0 ? totalDuration / sessions.length : 0;

    // Calculate average events per session
    const totalEvents = sessionsEventsCount.reduce((acc, count) => acc + count, 0);
    const averageEventsPerSession = sessions.length > 0 ? totalEvents / sessions.length : 0;

    return {
      totalSessions: sessions.length,
      averageSessionDuration,
      averageEventsPerSession,
      sessionDurations,
      sessionsByHour,
      sessionsEventsCount,
      sessions
    };
  }, [logs]);

  // Duration distribution chart data
  const durationChartData = useMemo(() => {
    const durations = processedData.sessionDurations;
    
    // Create buckets for duration distribution
    const buckets = [
      { label: '< 1 min', count: 0, max: 1 },
      { label: '1-5 mins', count: 0, max: 5 },
      { label: '5-15 mins', count: 0, max: 15 },
      { label: '15-30 mins', count: 0, max: 30 },
      { label: '30-60 mins', count: 0, max: 60 },
      { label: '> 60 mins', count: 0, max: Infinity }
    ];
    
    durations.forEach(duration => {
      for (let i = 0; i < buckets.length; i++) {
        if (duration <= buckets[i].max || i === buckets.length - 1) {
          buckets[i].count++;
          break;
        }
      }
    });
    
    return {
      labels: buckets.map(b => b.label),
      datasets: [
        {
          label: tFunction('session_duration') || 'Session Duration',
          data: buckets.map(b => b.count),
          backgroundColor: '#3498db',
          borderColor: '#2980b9',
          borderWidth: 1,
        }
      ]
    };
  }, [processedData.sessionDurations, tFunction]);

  // Hour distribution chart data
  const hourChartData = useMemo(() => {
    return {
      labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
      datasets: [
        {
          label: tFunction('sessions_by_hour') || 'Sessions by Hour',
          data: processedData.sessionsByHour,
          backgroundColor: '#2ecc71',
          borderColor: '#27ae60',
          borderWidth: 1,
        }
      ]
    };
  }, [processedData.sessionsByHour, tFunction]);

  // Events per session distribution chart data
  const eventsCountChartData = useMemo(() => {
    const counts = processedData.sessionsEventsCount;
    
    // Create buckets for events count distribution
    const buckets = [
      { label: '1 event', count: 0, max: 1 },
      { label: '2-5 events', count: 0, max: 5 },
      { label: '6-10 events', count: 0, max: 10 },
      { label: '11-20 events', count: 0, max: 20 },
      { label: '21-50 events', count: 0, max: 50 },
      { label: '> 50 events', count: 0, max: Infinity }
    ];
    
    counts.forEach(count => {
      for (let i = 0; i < buckets.length; i++) {
        if (count <= buckets[i].max || i === buckets.length - 1) {
          buckets[i].count++;
          break;
        }
      }
    });
    
    return {
      labels: buckets.map(b => b.label),
      datasets: [
        {
          label: tFunction('events_per_session') || 'Events per Session',
          data: buckets.map(b => b.count),
          backgroundColor: '#e74c3c',
          borderColor: '#c0392b',
          borderWidth: 1,
        }
      ]
    };
  }, [processedData.sessionsEventsCount, tFunction]);

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#24292e',
        bodyColor: '#24292e',
        borderColor: '#eaecef',
        borderWidth: 1,
        padding: 12,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      }
    }
  };

  // Empty state handling
  if (processedData.totalSessions === 0) {
    return (
      <div className="session-analytics empty-state">
        <h3>{tFunction('session_analytics') || 'Session Analytics'}</h3>
        <p>{tFunction('no_session_data') || 'No session data available for the selected logs.'}</p>
      </div>
    );
  }

  return (
    <div className="session-analytics">
      <h3>{tFunction('session_analytics') || 'Session Analytics'}</h3>
      
      <div className="session-summary">
        <div className="summary-item">
          <div className="summary-label">{tFunction('total_sessions') || 'Total Sessions'}</div>
          <div className="summary-value">{processedData.totalSessions}</div>
        </div>
        <div className="summary-item">
          <div className="summary-label">{tFunction('avg_session_duration') || 'Avg. Session Duration'}</div>
          <div className="summary-value">
            {processedData.averageSessionDuration.toFixed(1)} {tFunction('minutes') || 'mins'}
          </div>
        </div>
        <div className="summary-item">
          <div className="summary-label">{tFunction('avg_events_per_session') || 'Avg. Events per Session'}</div>
          <div className="summary-value">{processedData.averageEventsPerSession.toFixed(1)}</div>
        </div>
      </div>
      
      <div className="charts-container">
        <div className="chart-section">
          <h4>{tFunction('session_duration_distribution') || 'Session Duration Distribution'}</h4>
          <div className="chart-wrapper">
            <Bar data={durationChartData} options={chartOptions} />
          </div>
        </div>
        
        <div className="chart-section">
          <h4>{tFunction('sessions_by_hour') || 'Sessions by Hour of Day'}</h4>
          <div className="chart-wrapper">
            <Line data={hourChartData} options={chartOptions} />
          </div>
        </div>
        
        <div className="chart-section">
          <h4>{tFunction('events_per_session_distribution') || 'Events per Session Distribution'}</h4>
          <div className="chart-wrapper">
            <Bar data={eventsCountChartData} options={chartOptions} />
          </div>
        </div>
      </div>
    </div>
  );
} 